// Awakened forms (shared/awakening.ts): data only, so shared/species.ts can build them without an import cycle.
//
// A curated set of final-stage species, two per region (6 of our 93 species, about the reference game's 59 of
// ~1,000), each an iconic or strong final form. The awakened base stats REPLACE the species' own (they are not a
// multiplier) and total 100 more, as the reference's do; elements may change. Talent, quality, Bloodline, Aura,
// refine and stars still apply on top. Stat order: HP, Attack, Defense, Magic, Resist, Speed.
//
// Where the +100 goes (sim/battle-bench.ts, awakened rows): +20 to the attack side it fights with, +5-10 to each
// bulk stat, +20 Speed, and the rest (30-40) to the attack side it does not use, as many reference forms raise both.
// With the 600-power move that keeps a form strong but not dominant in PvP: one awakened slot tips a mirrored 5v5
// about 4 in 5 (an Aura about 19 in 20) and wins at most ~22 points over its tier in a random team.
//
// Art hook: the awakened form's sprite id is `<species>-awakened` (its species id). Until art draws it, the client
// shows the base species' sprite with a gilt rune marker (client/assets.ts awakenedArt).
import type { Element } from './elements.ts';

export interface AwakeningDef {
  /** The final-stage species that can awaken. */
  from: string;
  name: string;
  /** Elements after awakening; the first is the Awakened move's element. */
  elements: Element[];
  /** New base stats, replacing the species' own: HP, Attack, Defense, Magic, Resist, Speed. */
  base: [number, number, number, number, number, number];
  /** Name of its Awakened move (600 power, 30 s cooldown). */
  move: string;
  /** A line for the art thread: how the awakened form differs from the base sprite. */
  look: string;
}

export const AWAKENINGS: AwakeningDef[] = [
  // Midgard
  { from: 'fenrisborn', name: 'Awakened Fenrisborn', elements: ['frost', 'shade'], base: [72, 109, 77, 79, 54, 109], move: 'Fimbulwinter Fang',
    look: 'Fenrisborn with its chain fully shattered, a black-frost mane, a gilt rune collar and a halo of drifting ice runes' },
  { from: 'jormunspawn', name: 'Awakened Jormunspawn', elements: ['tide', 'shade'], base: [117, 124, 89, 132, 94, 104], move: 'Midgard Undertow',
    look: 'Jormunspawn grown into a crowned world-serpent, gilt rune bands along its coils, a whirlpool beneath it' },
  // Hellas
  { from: 'cerberus', name: 'Awakened Cerberus', elements: ['flame', 'shade'], base: [84, 152, 84, 93, 63, 125], move: 'Styx Conflagration',
    look: 'Cerberus wreathed in hellfire, molten cracks in its hide, gilt chains and a ring of flame runes behind its three heads' },
  { from: 'phoenix', name: 'Awakened Phoenix', elements: ['flame', 'light'], base: [89, 96, 89, 160, 122, 104], move: 'Ashen Sunrise',
    look: 'Phoenix with white-gold flames, a sunburst halo and long gilt tail streamers' },
  // Yomi
  { from: 'ninefold', name: 'Awakened Ninefold', elements: ['light', 'flame'], base: [90, 105, 90, 155, 95, 125], move: 'Heavenfox Radiance',
    look: 'Ninefold turned celestial: golden fur, nine tails of pale light and a floating gilt rune wheel' },
  { from: 'tempestsovereign', name: 'Awakened Tempest Sovereign', elements: ['tide', 'storm'], base: [112, 130, 85, 148, 90, 95], move: 'Dragon King\'s Thunder',
    look: 'Tempest Sovereign crowned in lightning, gilt scales along its spine and a storm-pearl orbit of runes' },
];

/** Species id of the awakened form of `from`: also its sprite id. */
export const awakenedId = (from: string) => `${from}-awakened`;
